import { useCallback, useContext, useState, Dispatch } from 'react';
import _get from 'lodash/get';
import _find from 'lodash/find';

import { AjaxContext, formatListData, AppContext, UserContext, EMPTY_STRING } from 'commons-ui';
import { UserDetailsContext } from '../../../context/userDetails';
import { ticketInputs } from './actions';
import { ACTION, STATUS_CODE, USER_PROFILE_ACL_ACTIONS } from '../../../constant';
import { EURL } from '../../../enum';

const useService = (setForceUpdate: Dispatch<boolean>) => {
	const { postData } = useContext(AjaxContext);
	const { app } = useContext(AppContext);
	const { permissionsConfig } = useContext(UserContext);
	const {
		selectedUser: { uid, ludoUserId, deviceType }
	} = useContext(UserDetailsContext);
	const [showLoader, setShowLoader] = useState(false);
	const { showAlert, campaignOptions, setCampaignOptions } = useContext(AppContext);

	const getPreviousTicketsData = useCallback(async () => {
		const { isGold, appName } = app;
		const permission = _find(permissionsConfig, { actionName: ACTION.listHelpdeskTickets });
		const result = await postData({
			endpoint: EURL.LIST_HELPDESK_TICKETS,
			queryParams: {
				appName
			},
			body: {
				zupeeUserId: uid,
				isGold,
				limit: 5000,
				status: 'all'
			},
			customHeaders: {
				isMasked: permission?.isMasked || false
			}
		});

		const data = _get(result, ['data', 'data', 'helpdeskTickets'], {});

		const listData = formatListData(data);
		const rowCount = _get(result, ['data', 'data', 'helpdeskTickets', 'count'], 0);

		return { rowCount, data: listData };
	}, []);

	const makeUserSupportAutomatedCall = useCallback(
		async (ticketId: number, campaignName: string, agentId: string, phoneType?: 'PRIMARY' | 'ALTERNATE') => {
			const { appName } = app;
			const permission = _find(permissionsConfig, { actionName: USER_PROFILE_ACL_ACTIONS.callUser });
			try {
				const result = await postData({
					endpoint: EURL.CALL_USER,
					queryParams: {
						appName
					},
					body: {
						uid,
						ticketId,
						agentID: agentId,
						campaignName,
						phoneType
					},
					customHeaders: {
						isMasked: permission?.isMasked || false
					}
				});
				if (!result || !result?.data) {
					throw new Error(result?.data?.error || 'User not authorized');
				}
				if (result?.data?.success === false) {
					throw new Error(result?.data?.data?.message || 'User not authorized');
				}
				return _get(result.data, ['data', 'message']) || _get(result.data, ['data'], EMPTY_STRING);
			} catch (error: any) {
				const errorMessage: string = error?.response?.data?.error || error?.message;
				showAlert(errorMessage || 'An error occurred while making the call.', { type: 'error' });
				return EMPTY_STRING;
			}
		},
		[]
	);

	const createTicket = useCallback(async (filtersData: ticketInputs) => {
		const { appName } = app;
		const permission = _find(permissionsConfig, { actionName: 'createHelpdeskTicket' });
		const { topicId, subTopicId, entryFee, description, transactionId } = filtersData;
		try {
			const result = await postData({
				endpoint: EURL.CREATE_HELPDESK_TICKET,
				queryParams: {
					appName
				},
				body: {
					zupeeUserId: uid,
					uid: ludoUserId,
					topicId,
					subTopicId,
					transactionId,
					entryFee,
					subject: subTopicId,
					description,
					deviceType
				},
				customHeaders: {
					isMasked: permission?.isMasked || false
				}
			});
			if (!result || _get(result, 'status') === STATUS_CODE.FORBIDDEN) {
				throw new Error('User not authorized');
			}
			setForceUpdate(true);
			const data = _get(result, ['data', 'helpdeskTicket'], {});
			const restructuredData = [data];
			setShowLoader(false);
			showAlert('Ticket created successfully.');
			return { rowCount: restructuredData.length, data: formatListData(restructuredData) };
		} catch (error: any) {
			setForceUpdate(true);
			const errorMessage: string = error?.response?.data?.error || error?.message;
			showAlert(errorMessage, { type: 'error' });
			return { rowCount: 0, data: [] };
		} finally {
			setShowLoader(false);
		}
	}, []);

	const getCampaignOptions = useCallback(async () => {
		if (campaignOptions && campaignOptions.length > 0) {
			return campaignOptions;
		}
		const { appName } = app;
		const result = await postData({
			endpoint: EURL.GET_CAMPAIGNS,
			queryParams: {
				appName
			},
			customHeaders: {
				isMasked: false
			}
		});
		const data: Array<{ id: string; label: string }> = _get(result, ['data', 'data'], []);
		setCampaignOptions(data);
		return data;
	}, []);

	return { showLoader, getPreviousTicketsData, makeUserSupportAutomatedCall, createTicket, getCampaignOptions };
};

export default useService;
